import type {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

import { validateScoutAttachment } from "encamp-constants/scout";
import pluralize from "pluralize";

export {
  SCOUT_LARGE_PDF_MAX_BYTES,
  SCOUT_MAX_ATTACHMENTS,
  SCOUT_NATIVE_MAX_PDF_BYTES,
  SCOUT_NATIVE_TEXT_SYNC_THRESHOLD_BYTES,
  SCOUT_TEXT_EXTRACTION_CSV_MAX_BYTES,
  SCOUT_TEXT_EXTRACTION_OFFICE_MAX_BYTES,
} from "encamp-constants/scout";

const OFFICE_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/xml",
  "text/xml",
]);

const CSV_MIME_TYPES = new Set(["text/csv", "application/csv", "text/tab-separated-values"]);

/** MIME types accepted by Scout attachments (file picker + validation). */
export const SCOUT_ATTACHMENT_ACCEPT_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "text/plain",
  ...OFFICE_MIME_TYPES,
  ...CSV_MIME_TYPES,
] as const;

/**
 * File extensions for the native file picker `accept` attribute.
 * Browsers often mislabel Office files; extensions keep the dialog aligned with validation.
 */
export const SCOUT_ATTACHMENT_ACCEPT_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".txt",
  ".docx",
  ".xlsx",
  ".pptx",
  ".xml",
  ".csv",
  ".tsv",
] as const;

/** Value for `<input type="file" accept="...">` in Scout composer. */
export const SCOUT_ATTACHMENT_FILE_INPUT_ACCEPT = [
  ...SCOUT_ATTACHMENT_ACCEPT_MIME_TYPES,
  ...SCOUT_ATTACHMENT_ACCEPT_EXTENSIONS,
].join(",");

export function validateScoutAttachmentFile(file: File): string | undefined {
  return validateScoutAttachment({
    mediaType: file.type,
    sizeBytes: file.size,
    filename: file.name,
  });
}

export function formatScoutDocumentSizeBytes(sizeBytes: number | undefined): string | undefined {
  if (sizeBytes === undefined || sizeBytes <= 0) {
    return undefined;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(0)} KB`;
  }

  return `${sizeBytes} B`;
}

export function userFriendlyScoutMediaType(mediaType: string): string {
  switch (mediaType) {
    case "application/pdf":
      return "PDF";
    case "image/png":
      return "PNG";
    case "image/jpeg":
      return "JPEG";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "Word";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "Excel";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "PowerPoint";
    case "text/csv":
    case "application/csv":
      return "CSV";
    case "text/tab-separated-values":
      return "TSV";
    case "application/xml":
    case "text/xml":
      return "XML";
    default:
      return mediaType;
  }
}

export function formatScoutFileHoverMetadata(input: {
  mediaType?: string;
  sizeBytes?: number;
}): string | undefined {
  const typeLabel =
    input.mediaType !== undefined ? formatScoutFileHoverTypeLabel(input.mediaType) : undefined;
  const sizeLabel = formatScoutDocumentSizeBytes(input.sizeBytes);

  if (typeLabel && sizeLabel) {
    return `${typeLabel} · ${sizeLabel}`;
  }

  return typeLabel ?? sizeLabel;
}

function formatScoutFileHoverTypeLabel(mediaType: string): string | undefined {
  const normalizedMediaType = mediaType.toLowerCase();
  if (!normalizedMediaType || normalizedMediaType === "application/octet-stream") {
    return undefined;
  }

  const friendlyLabel = userFriendlyScoutMediaType(normalizedMediaType);
  if (friendlyLabel.includes("/")) {
    return undefined;
  }

  return friendlyLabel;
}

export function formatScoutFileMessageSize(sizeBytes: number | undefined): string | undefined {
  if (sizeBytes === undefined || sizeBytes <= 0) {
    return undefined;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${Math.round(sizeBytes / (1024 * 1024))} mb`;
  }

  if (sizeBytes >= 1024) {
    return `${Math.round(sizeBytes / 1024)} kb`;
  }

  return `${sizeBytes} b`;
}

export function formatScoutFileMessageMetadata(input: {
  sizeBytes?: number;
  pageCount?: number | null;
}): string | undefined {
  const sizeLabel = formatScoutFileMessageSize(input.sizeBytes);
  const pageCount = input.pageCount;
  const pageLabel =
    pageCount !== undefined && pageCount !== null && pageCount > 0
      ? `${pageCount} pages`
      : undefined;

  if (sizeLabel && pageLabel) {
    return `${sizeLabel} • ${pageLabel}`;
  }

  return sizeLabel ?? pageLabel;
}

export function inferScoutFileDetectedLabel(filename: string | undefined): string | undefined {
  if (!filename) {
    return undefined;
  }

  const text = filename.toLowerCase();

  if (
    text.includes("air permit") ||
    text.includes("title v") ||
    text.includes("titlev") ||
    text.includes("operating permit")
  ) {
    return "Air Permit";
  }

  if (text.includes("sds") || text.includes("safety data sheet") || text.includes("msds")) {
    return "Safety Data Sheet";
  }

  if (text.includes("tier ii") || text.includes("tier 2") || text.includes("epcra")) {
    return "Tier II Report";
  }

  if (text.includes("npdes") || text.includes("stormwater") || text.includes("swppp")) {
    return "Water Permit";
  }

  if (text.includes("rcra") || text.includes("hazardous waste") || text.includes("manifest")) {
    return "Hazardous Waste Document";
  }

  if (text.includes("inspection") || text.includes("notice of violation")) {
    return "Inspection Document";
  }

  if (text.includes("permit")) {
    return "Environmental Permit";
  }

  return undefined;
}

export function formatScoutFileSecondaryText(input: {
  mediaType: string;
  sizeBytes?: number;
  pageCount?: number | null;
  ingestionMode?: ScoutPrivateDocumentIngestionMode | string;
  extractionStatus?: ScoutPrivateDocumentExtractionStatus | string;
  extractionProgress?: {
    pagesCompleted: number;
    pagesTotal?: number | null;
  } | null;
}): { documentInfo: string; extractionInfo?: string } {
  const typeLabel = userFriendlyScoutMediaType(input.mediaType);
  const sizeLabel = formatScoutDocumentSizeBytes(input.sizeBytes);
  const baseLabel = sizeLabel
    ? `${typeLabel} · ${sizeLabel} ${input.pageCount ? `· ${input.pageCount} ${pluralize("page", input.pageCount)}` : ""}`
    : typeLabel;

  const isTextExtraction = input.ingestionMode === "LARGE_FILE_EXTRACTION";

  if (!isTextExtraction) {
    return { documentInfo: baseLabel };
  }

  if (input.extractionStatus === "PREVIEW_RUNNING") {
    return { documentInfo: baseLabel, extractionInfo: "Scanning preview…" };
  }

  if (input.extractionStatus === "AWAITING_CONFIRMATION") {
    return { documentInfo: baseLabel, extractionInfo: "Confirm to start full analysis" };
  }

  if (input.extractionStatus === "FULL_ANALYSIS_DECLINED") {
    return { documentInfo: baseLabel, extractionInfo: "Using preview only" };
  }

  if (input.extractionStatus === "PENDING" || input.extractionStatus === "RUNNING") {
    const progress = input.extractionProgress;
    if (progress?.pagesTotal && progress.pagesTotal > 0) {
      return {
        documentInfo: baseLabel,
        extractionInfo: `Analyzing page ${progress.pagesCompleted} of ${progress.pagesTotal}`,
      };
    }
    return { documentInfo: baseLabel, extractionInfo: "Full analysis in progress…" };
  }

  if (input.extractionStatus === "FAILED") {
    return { documentInfo: baseLabel, extractionInfo: "Analysis failed" };
  }

  if (input.extractionStatus === "NOT_ATTEMPTED") {
    return { documentInfo: baseLabel, extractionInfo: "Large file" };
  }

  return { documentInfo: baseLabel };
}
